import React, { useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { setStepOneData } from '../forms/registrationSlice';
import { RootState } from '../forms/store';
import { Link } from 'react-router-dom';

import { REGISTRATION_STEP_ONE } from './Validations';
import checkIcon from '../../resources/icons/check.svg';

const RegisterStepOne = ({ onNext }: { onNext: () => void }) => {
  const dispatch = useDispatch();
  const registrationData = useSelector(
    (state: RootState) => state.registration
  );

  const validatePasswordConditions = (password: string) => {
    return {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      digitOrSpecial: /[0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    };
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const email = urlParams.get('email');

    if (email) {
      dispatch(setStepOneData({ email, password: '', confirmPassword: '' }));
    }
  }, [dispatch]);

  return (
    <div className="w-[calc(100%*5/9)] flex flex-col justify-center">
      <div className="w-1/2 self-center">
        <h2 className="mb-8 text-3xl font-bold">Sign up to Build Connect</h2>

        <Formik
          initialValues={{
            email: registrationData.email,
            password: registrationData.password,
            confirmPassword: registrationData.confirmPassword,
          }}
          validationSchema={REGISTRATION_STEP_ONE}
          onSubmit={(values) => {
            dispatch(setStepOneData(values));
            onNext();
          }}
          enableReinitialize
        >
          {({ isValid, dirty, values }) => {
            const rules = validatePasswordConditions(values.password);
            const allPassed = Object.values(rules).every(Boolean);

            const Tick = ({ active }: { active: boolean }) => (
              <div
                className={`w-5 h-5 flex items-center justify-center rounded-full ${
                  active ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <img src={checkIcon} alt="tick" className="w-[10px] h-[8px]" />
              </div>
            );

            return (
              <Form>
                <div className="relative mb-4">
                  <label className="text-grey-700 block text-sm font-semibold mb-2">
                    Email*:
                  </label>
                  <Field
                    type="email"
                    name="email"
                    className="w-full p-3 border-2 rounded-lg"
                    placeholder="Enter your email"
                  />
                  <ErrorMessage
                    name="email"
                    component="div"
                    className="text-red-500"
                  />
                </div>

                <div className="relative mb-4">
                  <label className="text-grey-700 block text-sm font-semibold mb-2">
                    Password*:
                  </label>
                  <Field
                    type="password"
                    name="password"
                    placeholder="Create a password"
                    className="w-full p-3 border-2 rounded-lg"
                  />
                  <ErrorMessage
                    name="password"
                    component="div"
                    className="text-red-500"
                  />
                </div>

                <div className="relative mb-4">
                  <label className="block text-sm text-grey-700 font-semibold mb-2">
                    Re-enter password*:
                  </label>
                  <Field
                    type="password"
                    name="confirmPassword"
                    placeholder="Re-enter your password"
                    className="w-full p-3 border-2 rounded-lg"
                  />
                  <ErrorMessage
                    name="confirmPassword"
                    component="div"
                    className="text-red-500"
                  />
                </div>

                {/* ✅ Password condition checks */}
                <div className="mb-8 mt-2 space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    <Tick active={rules.length} />
                    <span
                      className={
                        rules.length ? 'text-green-600' : 'text-gray-400'
                      }
                    >
                      At least 8 characters
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Tick active={rules.uppercase} />
                    <span
                      className={
                        rules.uppercase ? 'text-green-600' : 'text-gray-400'
                      }
                    >
                      At least one uppercase letter
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Tick active={rules.lowercase} />
                    <span
                      className={
                        rules.lowercase ? 'text-green-600' : 'text-gray-400'
                      }
                    >
                      At least one lowercase letter
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Tick active={rules.digitOrSpecial} />
                    <span
                      className={
                        rules.digitOrSpecial
                          ? 'text-green-600'
                          : 'text-gray-400'
                      }
                    >
                      At least one number or special character
                    </span>
                  </div>
                </div>

                <button
                  className="blue-button w-full py-3 bg-blue-500 text-white font-semibold rounded-lg"
                  type="submit"
                  disabled={!(dirty && allPassed)}
                >
                  Continue
                </button>

                <div className="flex justify-center mt-4">
                  <small className="mr-2 block text-sm text-gray-500 mt-1">
                    Already have an account?
                  </small>
                  <Link className="text-blue-600 nav-link" to={'/login'}>
                    Log in
                  </Link>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default RegisterStepOne;
